import { useCallback, useContext, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import {
  AddObjs,
  DrivePanelState,
  GetCurrentObj,
  GetFilterText,
  GetLayout,
  GetLoadingStates,
  GetObjectResults,
  GetPanelState,
  SetLayout,
} from "../../../Redux/Reducers/redux-slice-drive";
import { GetTreeObj, AddChildrenToTreeObj, TreeContext } from "../../../Redux/Reducers/redux-slice-tree";
import { Box, Button, IconButton, Stack } from "@mui/material";
import ToggleLayout from "../../Misc/ToggleLayout";
import { useTranslation } from "react-i18next";
import { CreateNewFolder, FileUploadOutlined } from "@mui/icons-material";
import { DriveBreadcrumbs } from "../Common/drive-breadcrumbs";
import { IsPermissionGranted } from "../../../Core/Utils/asset-object-utils";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import { ModalContext } from "../../Modals";
import { cacheService } from "../../../Core/Api/services";
import ModalCreateFolder, { ModalCreateFolderElementProps } from "../../Modals/ModalCreateFolder/modal-create-folder";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import ModalChooseUploadMethod, {
  ModalChooseUploadMethodProps,
} from "../../Modals/ModalChooseUploadMethod/modal-choose-upload-method";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import ModalHttpUpload, { ModalHttpUploadProps } from "../../Modals/ModalHttpUpload/modal-http-upload";
import ModalAsperaUpload, { ModalAsperaUploadProps } from "../../Modals/ModalAsperaUpload/modal-aspera-upload";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { DriveContext } from "../Common/drive-navigate-handler";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";

export const DriveFolderHeader = () => {
  const folderHeaderRef = useRef<HTMLDivElement>(null);
  const driveContext = useContext(DriveContext);
  const headObjectId = driveContext?.headObjectId;
  const { t } = useTranslation();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const uploadInputRef = useRef<any>(undefined);
  const isCloud = cacheService.GetPlatformSettings()?.cloud ?? false;

  // Redux
  const dispatch = useAppDispatch();

  const layout = useAppSelector(GetLayout);
  const enlargedPanel = useAppSelector(GetPanelState) === DrivePanelState.Enlarged;
  const { breadcrumbsLoading } = useAppSelector(GetLoadingStates);
  const previewPanelOpen = useAppSelector(GetPanelState) === DrivePanelState.Preview;
  const treeObj = useAppSelector(GetTreeObj(TreeContext.DriveTreeview));
  const currentObject = useAppSelector(GetCurrentObj);
  const resultObjects = useAppSelector(GetObjectResults);
  const filterText = useAppSelector(GetFilterText);

  const aclFileUpload = UserHasAcl(AclTypes.aclMediaFileUpload);

  const userCanCreateFolder = useMemo(() => {
    if (!treeObj) return false;

    if (!aclFileUpload) return false;

    return IsPermissionGranted(treeObj, PermissionType.CreateObject);
  }, [treeObj, aclFileUpload]);

  const userCanUploadFiles = useMemo(() => {
    if (!treeObj) return false;

    return IsPermissionGranted(treeObj, PermissionType.CreateObject);
  }, [treeObj]);

  const handleCreateFolder = useCallback(() => {
    if (!openModal || !((currentObject && currentObject.id) ?? headObjectId)) return;

    openModal<ModalCreateFolderElementProps>(ModalCreateFolder, {
      parentObjectId: ((currentObject && currentObject.id) ?? headObjectId)!,
      onConfirm: (obj: DbAssetObjectInfos) => {
        dispatch(AddObjs([obj]));
        dispatch(
          AddChildrenToTreeObj({
            parentId: ((currentObject && currentObject.id) ?? headObjectId)!,
            children: [obj],
            context: TreeContext.DriveTreeview,
          })
        );
      },
      resultObjects: resultObjects,
    });
  }, [currentObject && currentObject.id, headObjectId, resultObjects]);

  const handleClickUploadFiles = (e) => {
    let parentId = (currentObject && currentObject.id) ?? headObjectId;
    if (!openModal || !parentId || !treeObj || !uploadInputRef.current) return;

    openModal<ModalChooseUploadMethodProps>(ModalChooseUploadMethod, {
      assetId: parentId,
      assetName: treeObj.name,
      assetParentName: treeObj.parentObject?.name || treeObj.parentTitleName || "",
      assetType: treeObj.type,
      assetClass: treeObj.classification ?? ObjectClass.Drive,
      closeModal: () => {},
      inputRef: uploadInputRef.current,
    });
  };

  const onInputFileChange = useCallback(
    (e) => {
      let parentId = (currentObject && currentObject.id) ?? headObjectId;

      if (!e.target || !e.target?.files) return;
      if (!parentId) return;
      if (e.target.files && e.target.files.length < 1) return;
      let shouldUseAspera = false;
      if (e.target.files.length > GlobalConst.MAX_FILES_COUNT_HTTP) shouldUseAspera = true;
      for (let i = 0; i < e.target.files.length; i++) {
        if (e.target.files[i].size > GlobalConst.MAX_FILE_SIZE_HTTP_UPLOAD) shouldUseAspera = true;
      }

      if (!shouldUseAspera) {
        openModal &&
          treeObj &&
          openModal<ModalHttpUploadProps>(ModalHttpUpload, {
            assetParentName: treeObj.parentObject?.name || treeObj.parentTitleName || "",
            assetId: parentId,
            assetName: treeObj.name,
            files: [...e.target.files],
            closeModal: () => {},
          });
      } else {
        openModal &&
          treeObj &&
          openModal<ModalAsperaUploadProps>(ModalAsperaUpload, {
            assetParentName: treeObj.parentObject?.name || treeObj.parentTitleName || "",
            assetId: parentId,
            assetName: treeObj.name,
            assetAnnexFormatIds: [],
            assetType: treeObj.type || ObjectType.Folder,
            assetClassification: ObjectClass.Raw,
            assetIsHdr: false,
            assetProviderId: 0,
            showWarning: true,
            useToken: isCloud,
            closeModal: () => {},
          });
      }
    },
    [currentObject && currentObject.id, headObjectId, treeObj]
  );

  return (
    <Box
      ref={folderHeaderRef}
      sx={{
        backgroundColor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" px={2}>
        <DriveBreadcrumbs />
        <Stack direction="row" gap={1} py={2}>
          {currentObject?.id && userCanCreateFolder && (
            <IconButton onClick={() => handleCreateFolder()} color="secondary" size="small">
              <CreateNewFolder />
            </IconButton>
          )}
          {currentObject && currentObject.id !== headObjectId && (
            <Button
              onClick={handleClickUploadFiles}
              variant="contained"
              color="primary"
              startIcon={<FileUploadOutlined />}
              size="small"
              disabled={!userCanUploadFiles}
            >
              <label style={{ cursor: "pointer" }} htmlFor="upload-file-but">
                {t("field:label.uploadFiles")}
              </label>
              <input
                value={[]}
                style={{ display: "none" }}
                id="upload-file-but"
                ref={uploadInputRef}
                accept={undefined}
                multiple={true}
                type="file"
                onClick={(e) => {
                  e.isTrusted && e.preventDefault();
                }}
                onChange={onInputFileChange}
              />
            </Button>
          )}
          <ToggleLayout
            value={layout}
            onChange={(e: React.MouseEvent<HTMLElement>, value: "grid" | "list") => dispatch(SetLayout(value))}
          />
        </Stack>
      </Stack>
    </Box>
  );
};
